/**
 * The Terminated worklists page's second section: open claims sitting on a "No assignment"
 * list, grouped by the biller who last held them (review #398, Shayna 2026-10-14).
 *
 * "No assignment" lists are system lists, so their claims count in nobody's pile and the
 * terminated list above never sees them. The builder walks each claim back through its
 * month ends (FactOpenClaims) to the last one on a real worklist with a person holding
 * it. That person is who to follow up with, terminated or not. A claim no month end
 * shows held is counted, and has nobody to follow up with.
 */
import { useMemo } from 'react'
import { useUrlState, bool, json } from '@/ui/urlState'
import type { NoAssignmentHolder, NoAssignmentSection as Section } from '@/data/types'
import { DataTable, type Column } from '@/components/Table'
import { StatStrip, Stat, PersonCell } from '@/components/Bits'
import { ExportBar } from '@/components/ExportBar'
import { openClaimColumns, OPEN_CLAIM_EXPORT } from '@/components/OpenClaimTable'
import { PopOut } from '@/components/PopOut'
import { SidePanel } from '@/components/SidePanel'
import {
  decodeNoAssignmentClaims,
  hasOrgTotals,
  holderTotals,
  movedBy,
  type NoAssignmentClaim,
} from '@/data/noAssignment'
import type { ExportColumn } from '@/ui/export'
import { dateLong, int, monthYear } from '@/format'
import { useOpenClaimVisit } from '@/data/claimDrill'
import { usePageAllowed } from '@/access/pageLimit'

const matches = (sel: string[], value: string) => sel.length === 0 || sel.includes(value)
const MAX_ROWS = 500

const days = (n: number | null | undefined) => (n == null ? '—' : `${int(n)} days`)

const statusOf = (h: NoAssignmentHolder) =>
  h.status === 'terminated' ? `Terminated ${h.terminationDate ?? ''}`.trim() : 'Active'

export function NoAssignmentSection({
  section,
  department,
  jobTitle,
  snapshot,
  asOf,
}: {
  section: Section
  department: string[]
  jobTitle: string[]
  snapshot: string | null
  asOf: string | null
}) {
  const [termOnly, setTermOnly] = useUrlState('na.term', false, bool)
  const [opened, setOpened] = useUrlState(
    'na.claims',
    null as { employee: string } | null,
    json<{ employee: string } | null>(null),
    { personal: true },
  )

  const holders = useMemo(
    () =>
      section.holders.filter(
        (h) =>
          matches(department, h.department) &&
          matches(jobTitle, h.jobTitle) &&
          (!termOnly || h.status === 'terminated'),
      ),
    [section, department, jobTitle, termOnly],
  )
  const t = useMemo(() => holderTotals(holders), [holders])
  const org = hasOrgTotals(section)
  const open = opened ? section.holders.find((h) => h.employee === opened.employee) : undefined

  const columns: Column<NoAssignmentHolder>[] = [
    {
      key: 'person',
      header: 'Last assigned biller',
      width: 210,
      sort: (h) => h.employee,
      render: (h) => <PersonCell name={h.employee} meta={h.jobTitle} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      sort: (h) => (h.status === 'terminated' ? `0${h.terminationDate}` : '1'),
      render: (h) =>
        h.status === 'terminated' ? (
          <span>
            <span className="pts-chip warn">terminated</span>{' '}
            <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
              {dateLong(h.terminationDate)}
            </span>
          </span>
        ) : (
          <span className="pts-chip neut">active</span>
        ),
    },
    {
      key: 'department',
      header: 'Department',
      width: 130,
      sort: (h) => h.department,
      render: (h) => h.department,
    },
    {
      key: 'previous',
      header: 'Held them on',
      title: 'The worklist each claim was on at the last month end this person held it',
      sort: (h) => h.previousWorklists[0]?.name ?? '',
      render: (h) => (
        <ul className="tw-list">
          {h.previousWorklists.map((w) => (
            <li key={w.name}>
              <span className="tw-name">{w.name}</span>
              <span />
              <span className="mono tw-count">{int(w.openClaims)}</span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'moved',
      header: 'Moved to No assignment',
      width: 190,
      title:
        'Month-end data: a claim moved between the last month end the person held it and this one',
      sort: (h) => h.moved[h.moved.length - 1]?.month ?? '',
      render: (h) => (
        <ul className="tw-list">
          {h.moved.map((m) => (
            <li key={m.month}>
              <span className="tw-name">{movedBy(m.month, snapshot, asOf)}</span>
              <span />
              <span className="mono tw-count">{int(m.openClaims)}</span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'oldest',
      header: 'Oldest',
      align: 'right',
      width: 90,
      title: 'Days from the oldest claim’s bill date (date of service if unbilled) to the snapshot',
      sort: (h) => h.oldestDays,
      render: (h) => <span className="mono">{days(h.oldestDays)}</span>,
    },
    {
      key: 'open',
      header: 'Open claims',
      align: 'right',
      width: 100,
      sort: (h) => h.openClaims,
      render: (h) => (
        <button
          type="button"
          className="mono tw-count tw-open"
          onClick={() => setOpened({ employee: h.employee })}
          title={`Show these ${int(h.openClaims)} open claims`}
        >
          {int(h.openClaims)}
        </button>
      ),
    },
  ]

  return (
    <>
      <h2 className="section-heading" style={{ marginTop: 32 }}>
        Claims on No assignment lists
      </h2>
      <p className="body-note" style={{ maxWidth: 760, marginTop: 6 }}>
        A “No assignment” list is a system list: its claims count in nobody’s open pile, so
        no biller is working them and they are not on the list above. Each claim is traced
        back through the month-end snapshots to the last biller who held it on a real
        worklist. That is who to follow up with, whether they have left or not.
      </p>

      <div style={{ marginTop: 16 }}>
        <StatStrip cols={4}>
          <Stat
            label="On No assignment lists"
            value={int(org ? (section.openClaims ?? 0) : t.claims)}
            note={
              org
                ? `oldest ${days(section.oldestDays)}${snapshot ? `, ${monthYear(snapshot)} snapshot` : ''}`
                : 'last held by people in your access'
            }
            hero
          />
          <Stat
            label="Last held by a biller"
            value={int(t.claims)}
            note={`${int(t.people)} ${t.people === 1 ? 'person' : 'people'}`}
          />
          <Stat
            label="Of those, terminated"
            value={int(t.terminatedClaims)}
            note={`${int(t.terminated)} ${t.terminated === 1 ? 'person' : 'people'}`}
          />
          {org && section.unheld && (
            <Stat
              label="Never held by anyone"
              value={int(section.unheld.openClaims)}
              note="no biller to follow up with"
            />
          )}
        </StatStrip>
      </div>
      {org && section.lists && section.lists.length > 0 && (
        <p className="body-note" style={{ marginTop: 8 }}>
          By list:{' '}
          {section.lists.map((l, i) => (
            <span key={l.name}>
              {i > 0 && ' · '}
              <span className="tw-name">{l.name}</span> {int(l.openClaims)}
            </span>
          ))}
        </p>
      )}

      <PopOut title="Who held them last" style={{ marginTop: 16 }}>
        {({ button, enlarged }) => (
          <div className="card popout-fill">
            <div className="tw-head">
              {!enlarged && <h3 className="card-heading">Who held them last</h3>}
              <label className="tw-toggle">
                <input
                  type="checkbox"
                  checked={termOnly}
                  onChange={(e) => setTermOnly(e.target.checked)}
                />
                Only terminated billers
              </label>
              {button}
            </div>
            <ExportBar filename="no-assignment-followup.csv" rows={holders} columns={HOLDER_EXPORT} />
            <div className="card emp-dense" style={{ padding: 0, overflow: 'hidden' }}>
              <DataTable
                storageKey="no-assignment.people"
                rows={holders}
                columns={columns}
                rowKey={(h) => h.fccKeys.join(',')}
                initialSort="open"
                initialDir="desc"
                sortParam="na.sort"
                maxHeight={640}
                empty="No claim on a No assignment list was last held by anyone in this scope."
              />
            </div>
            <p className="body-note" style={{ marginTop: 10 }}>
              Held means the claim was on a non-system worklist in that person’s open pile at
              a month end. The data is month-end snapshots, so a move is known to the month: it
              happened after the last month end the person held the claim.
            </p>
          </div>
        )}
      </PopOut>

      {open && (
        <HolderClaimsPanel
          holder={open}
          snapshot={snapshot}
          asOf={asOf}
          onClose={() => setOpened(null)}
        />
      )}
    </>
  )
}

/** One row per biller: the list someone follows up from. The weekly email's CSV matches. */
export const HOLDER_EXPORT: ExportColumn<NoAssignmentHolder>[] = [
  { header: 'Last assigned biller', value: (h: NoAssignmentHolder) => h.employee },
  { header: 'Paylocity ID', value: (h: NoAssignmentHolder) => h.paylocityId ?? '' },
  { header: 'Status', value: (h: NoAssignmentHolder) => (h.status === 'terminated' ? 'Terminated' : 'Active') },
  { header: 'Termination date', value: (h: NoAssignmentHolder) => h.terminationDate ?? '' },
  { header: 'Job title', value: (h: NoAssignmentHolder) => h.jobTitle },
  { header: 'Department', value: (h: NoAssignmentHolder) => h.department },
  {
    header: 'Held them on',
    value: (h: NoAssignmentHolder) =>
      h.previousWorklists.map((w) => `${w.name} (${w.openClaims})`).join('; '),
  },
  { header: 'Last month end held', value: (h: NoAssignmentHolder) => h.lastHeld },
  {
    header: 'Moved by month end',
    value: (h: NoAssignmentHolder) => h.moved.map((m) => `${m.month} (${m.openClaims})`).join('; '),
  },
  {
    header: 'No assignment list now',
    value: (h: NoAssignmentHolder) => h.lists.map((l) => `${l.name} (${l.openClaims})`).join('; '),
  },
  { header: 'Open claims', value: (h: NoAssignmentHolder) => h.openClaims },
  { header: 'Oldest open claim (days)', value: (h: NoAssignmentHolder) => h.oldestDays ?? '' },
]

function HolderClaimsPanel({
  holder,
  snapshot,
  asOf,
  onClose,
}: {
  holder: NoAssignmentHolder
  snapshot: string | null
  asOf: string | null
  onClose: () => void
}) {
  const claims = useMemo(() => decodeNoAssignmentClaims(holder.claims), [holder])
  const openVisit = useOpenClaimVisit()
  const toClaims = usePageAllowed('/claim-detail')
  const base = openClaimColumns(true, toClaims ? openVisit : undefined) as Column<NoAssignmentClaim>[]
  // Beside the worklist it is on now, where the eye is: the two things to follow up.
  const at = base.findIndex((c) => c.key === 'worklist') + 1
  const columns: Column<NoAssignmentClaim>[] = [
    ...base.slice(0, at),
    {
      key: 'prev',
      header: 'Held on',
      width: 200,
      sort: (c) => c.previousWorklist,
      render: (c) => <span className="tw-name">{c.previousWorklist || '—'}</span>,
    },
    {
      key: 'moved',
      header: 'Moved',
      width: 130,
      sort: (c) => c.moved,
      render: (c) => movedBy(c.moved, snapshot, asOf),
    },
    ...base.slice(at),
  ]
  return (
    <SidePanel onClose={onClose} label={`No assignment claims last held by ${holder.employee}`}>
      <span className="eyebrow">
        On No assignment lists{snapshot ? `, ${monthYear(snapshot)} snapshot` : ''}
      </span>
      <h2 className="page-title" style={{ marginTop: 4 }}>
        {holder.employee}
      </h2>
      <p className="body-note" style={{ marginTop: 4 }}>
        {holder.jobTitle} · {statusOf(holder)}
        {holder.paylocityId ? ` · Paylocity ID ${holder.paylocityId}` : ''}
      </p>
      <ExportBar
        filename="no-assignment-claims.csv"
        rows={claims}
        columns={[
          { header: 'Last assigned biller', value: () => holder.employee },
          { header: 'Paylocity ID', value: () => holder.paylocityId ?? '' },
          ...OPEN_CLAIM_EXPORT,
          { header: 'Held on worklist', value: (c: NoAssignmentClaim) => c.previousWorklist },
          { header: 'Last month end held', value: (c: NoAssignmentClaim) => c.held },
          { header: 'Moved by month end', value: (c: NoAssignmentClaim) => c.moved },
        ]}
      />
      <div className="card emp-dense" style={{ padding: 0, overflow: 'hidden' }}>
        <DataTable
          storageKey="no-assignment.claims"
          sortParam="na.claims.sort"
          rows={claims}
          columns={columns}
          rowKey={(c) => c.claimId}
          initialSort="bill"
          initialDir="asc"
          maxRows={MAX_ROWS}
          maxHeight="calc(100vh - 260px)"
          empty="No open claims here."
        />
      </div>
      <p className="body-note" style={{ marginTop: 10 }}>
        {claims.length > MAX_ROWS &&
          `The table shows the first ${int(MAX_ROWS)} in the current sort; the export has all ${int(claims.length)}. `}
        Worklist is the No assignment list the claim is on now; Held on is the worklist this
        person held it on at the last month end before it moved.
      </p>
    </SidePanel>
  )
}
